// Every file picker in the app (round 45, Nathan: "make file uploading allow
// for drag and drop and browsing" across the board).
//
// Zanetto Tools' FileDropInput, ported: a dashed zone that takes a drop, and
// opens the browser's picker when clicked, tapped or given Enter/Space. The
// <input type="file"> stays hidden behind it (round 33: a customer never sees
// the native control).
//
// A dropped file skips the input's `accept` filter (the browser only applies
// it to the picker), so the zone checks a drop itself and names the file it
// refused, before anything is sent. The server still decides; this only
// spares a person a round trip for a file that was never going to be taken.
//
// Two shapes: `zone` (the default, the dashed box) and `button` (an ordinary
// button that ALSO takes a drop, for places where a whole zone would crowd
// the screen: a page header's Import CSV, the logo, a form's photo question).
import { useId, useRef, useState, type DragEvent, type KeyboardEvent, type ReactNode } from 'react';

export interface FileDropProps {
  /** The input's accept string, e.g. '.pdf,image/*'. Also checks a drop. */
  accept?: string;
  multiple?: boolean;
  /** Kept ON the hidden input, so a phone's photo question opens the camera. */
  capture?: 'environment' | 'user';
  disabled?: boolean;
  onFiles: (files: File[]) => void;
  /** Called with a sentence naming a dropped file the accept list refuses. */
  onReject?: (message: string) => void;
  /** What the accepted kinds are called in a refusal: 'PDF or image'. */
  typeLabel?: string;
  variant?: 'zone' | 'button';
  /** The zone's main line, or the button's label. */
  label?: ReactNode;
  /** A line under the zone's main line (the zone variant only). */
  hint?: ReactNode;
  /** Classes for the button variant. */
  buttonClassName?: string;
  /** The hidden input's id, so a <label htmlFor> can name it. */
  id?: string;
  'aria-label'?: string;
}

/** Does this file match an accept string? Extension or MIME, wildcards too. */
export function acceptsFile(accept: string | undefined, file: File): boolean {
  if (!accept) return true;
  const tokens = accept
    .split(',')
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);
  if (tokens.length === 0) return true;
  const name = file.name.toLowerCase();
  const type = (file.type || '').toLowerCase();
  return tokens.some((token) => {
    if (token.startsWith('.')) return name.endsWith(token);
    if (token.endsWith('/*')) return type.startsWith(token.slice(0, -1));
    return type === token;
  });
}

export function FileDrop({
  accept,
  multiple = false,
  capture,
  disabled = false,
  onFiles,
  onReject,
  typeLabel,
  variant = 'zone',
  label,
  hint,
  buttonClassName = 'btn-secondary btn-sm',
  id,
  'aria-label': ariaLabel,
}: FileDropProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const generated = useId();
  const inputId = id ?? `file-${generated}`;
  const [dragActive, setDragActive] = useState(false);
  // dragenter/dragleave fire for every child the pointer crosses; a counter
  // keeps the highlight from flickering off over the icon.
  const depth = useRef(0);

  function open() {
    if (!disabled) inputRef.current?.click();
  }

  function deliver(list: FileList | File[] | null, fromDrop: boolean) {
    const all = Array.from(list ?? []);
    if (all.length === 0) return;
    const chosen = multiple ? all : all.slice(0, 1);
    if (fromDrop) {
      const refused = chosen.filter((file) => !acceptsFile(accept, file));
      if (refused.length > 0) {
        const names = refused.map((f) => `"${f.name}"`).join(', ');
        onReject?.(
          `${names} ${refused.length === 1 ? "isn't" : "aren't"} a file this takes${
            typeLabel ? `: ${typeLabel} only` : ''
          }.`
        );
        const kept = chosen.filter((file) => acceptsFile(accept, file));
        if (kept.length > 0) onFiles(kept);
        return;
      }
    }
    onFiles(chosen);
  }

  const dragHandlers = {
    onDragEnter: (e: DragEvent) => {
      if (disabled) return;
      e.preventDefault();
      depth.current += 1;
      setDragActive(true);
    },
    onDragOver: (e: DragEvent) => {
      if (disabled) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    },
    onDragLeave: (e: DragEvent) => {
      if (disabled) return;
      e.preventDefault();
      depth.current = Math.max(0, depth.current - 1);
      if (depth.current === 0) setDragActive(false);
    },
    onDrop: (e: DragEvent) => {
      e.preventDefault();
      depth.current = 0;
      setDragActive(false);
      if (disabled) return;
      deliver(e.dataTransfer.files, true);
    },
  };

  const input = (
    <input
      id={inputId}
      ref={inputRef}
      type="file"
      className="hidden"
      accept={accept}
      multiple={multiple}
      {...(capture ? { capture } : {})}
      disabled={disabled}
      data-testid="file-drop-input"
      onChange={(e) => {
        deliver(e.target.files, false);
        e.target.value = '';
      }}
    />
  );

  if (variant === 'button') {
    return (
      <>
        <button
          type="button"
          className={`${buttonClassName} ${dragActive ? 'ring-2 ring-brand-500 ring-offset-1' : ''}`}
          disabled={disabled}
          onClick={open}
          aria-label={ariaLabel}
          {...dragHandlers}
        >
          {label ?? 'Choose file'}
        </button>
        {input}
      </>
    );
  }

  function onKeyDown(e: KeyboardEvent) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      open();
    }
  }

  return (
    <div>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        aria-label={ariaLabel ?? (typeof label === 'string' ? label : 'Choose files')}
        onClick={open}
        onKeyDown={onKeyDown}
        {...dragHandlers}
        className={`file-drop flex min-h-11 cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed px-4 py-5 text-center transition-colors ${
          disabled
            ? 'cursor-not-allowed border-gray-200 bg-gray-50 opacity-60'
            : dragActive
              ? 'border-brand-500 bg-brand-50'
              : 'border-gray-300 bg-gray-50 hover:border-brand-400 hover:bg-brand-50/50'
        }`}
        data-drag-active={dragActive ? 'true' : undefined}
      >
        <svg
          aria-hidden="true"
          className="h-6 w-6 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
          />
        </svg>
        <span className="text-sm text-gray-600">
          {label ?? (
            <>
              <span className="max-sm:hidden">Drag and drop {multiple ? 'files' : 'a file'} here, or </span>
              <span className="font-medium text-brand-800 underline underline-offset-2">
                <span className="sm:hidden">Choose {multiple ? 'files' : 'a file'}</span>
                <span className="max-sm:hidden">browse</span>
              </span>
            </>
          )}
        </span>
        {hint && <span className="text-xs text-gray-500">{hint}</span>}
      </div>
      {input}
    </div>
  );
}

/**
 * One file held by a form until it is saved (a record's scan, an insurance
 * certificate, a purchase order, an invoice). The zone names the file once
 * chosen; dropping or choosing again replaces it, and Remove clears it.
 */
export function SingleFileDrop({
  file,
  onChange,
  accept,
  typeLabel,
  id,
  onReject,
  disabled,
}: {
  file: File | null;
  onChange: (file: File | null) => void;
  accept?: string;
  typeLabel?: string;
  id?: string;
  onReject?: (message: string) => void;
  disabled?: boolean;
}) {
  return (
    <div>
      <FileDrop
        id={id}
        accept={accept}
        typeLabel={typeLabel}
        disabled={disabled}
        onReject={onReject}
        onFiles={(files) => onChange(files[0] ?? null)}
        label={file ? <span className="font-medium text-gray-900">{file.name}</span> : undefined}
        hint={file ? 'Drop or choose another to replace it.' : undefined}
      />
      {file && (
        <button type="button" className="row-action mt-1" onClick={() => onChange(null)}>
          Remove
        </button>
      )}
    </div>
  );
}
